import { type EquipmentSubtypes, hasEquipmentSubtypes, isOfferExpired } from 'core/domain/Load'
import { getDelivery, getPickup } from 'core/domain/Stop'
import { differenceInHours, differenceInMinutes, formatDate } from 'core/utils/date'
import { round } from 'core/utils/number'

import { type Load } from '../domain/Load'

const rateTypeMap = {
  Contract: 'Contract',
  Rank: 'Contract',
  Spot: 'Spot',
}

export function formatTenderRejectText(enableTenderRejectReason: boolean) {
  if (enableTenderRejectReason) return 'Why do you want to reject this tender?'
  return 'Do you want to reject this tender?'
}

export function getHeaderTitle(load: Load) {
  const { deadline, tender } = load
  const { rateType, status } = tender

  const tenderString = `${rateTypeMap[rateType]} tender`

  if (status === 'accepted') return `${tenderString} accepted.`
  if (status === 'rejected') return `${tenderString} rejected.`
  if (status === 'unavailable') return `${tenderString} canceled by the shipper.`

  const isExpired = isOfferExpired(load)

  if (isExpired)
    return `${tenderString} expired on ${formatDate(deadline!, 'MMM dd, yyy - HH:MMa')}.`

  return tenderString
}

export function formatDeadlineText({ deadline }: Load) {
  const now = new Date()
  const diffHours = differenceInHours(deadline!, now)
  const diffMinutes = differenceInMinutes(deadline!, now) % 60

  const hoursString = diffHours.toString().padStart(2, '0')
  const minutesString = diffMinutes.toString().padStart(2, '0')

  return `Offer expires in ${hoursString}h${minutesString}m`
}

export function getProgressBarValue({ deadline, createdAt }: Load) {
  const total = differenceInMinutes(deadline!, createdAt!)
  const remaining = differenceInMinutes(deadline!, new Date())

  return round((remaining * 100) / total, 0)
}

export function getCoordinatesFromStops({ stops }: Load) {
  const pickup = getPickup(stops)
  const delivery = getDelivery(stops)

  return {
    puCoordinate: {
      lat: Number(pickup.latitude),
      lng: Number(pickup.longitude),
    },
    delCoordinate: {
      lat: Number(delivery.latitude),
      lng: Number(delivery.longitude),
    },
  }
}

export const equipmentSubtypes: Record<EquipmentSubtypes, string> = {
  regular_flatbed: 'Regular Flatbed',
  conestoga: 'Conestoga',
  step_deck: 'Step Deck',
  hotshot: 'Hotshot',
  double_drop: 'Double Drop',
  gooseneck: 'Gooseneck',
  low_boy: 'Low Boy',
  maxi: 'Maxi',
  power_only: 'Power Only',
  removable_gooseneck: 'Removable Gooseneck',
  conestoga_step_deck: 'Conestoga Step Deck',
}

export function formatSubEquipment(load: Load) {
  if (!load?.equipmentSubtypes || !hasEquipmentSubtypes(load)) {
    return null
  }

  const firstSubEquipment = load.equipmentSubtypes[0]

  return equipmentSubtypes[firstSubEquipment]
}
